import React, { useState, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Calculator,
  ArrowRight,
  Phone,
  Mail,
  CheckCircle,
  Star,
  Shield,
  Clock,
  Users,
  Home,
  Ruler,
  Award,
  Heart,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Calendar
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const LandingPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [showChoiceModal, setShowChoiceModal] = useState(false);
  const [selectedWindowCount, setSelectedWindowCount] = useState(null);
  const navigate = useNavigate();
  const quoteFormRef = useRef(null);

  const scrollToQuoteForm = () => {
    quoteFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const handleWindowSelect = (option) => {
    setSelectedWindowCount(option);
    setShowChoiceModal(true);
  };

  const handleGetEstimate = () => {
    setShowChoiceModal(false);
    navigate('/calculator');
  };

  const handleBookVisit = () => {
    setShowChoiceModal(false);
    navigate('/book-home-visit', { state: { windowCount: selectedWindowCount } });
  };

  const trustBadges = [
    { icon: Heart, text: 'Family Owned Since 2018' },
    { icon: Shield, text: '10 Year Structural Warranty' },
    { icon: Star, text: '5 Star Google Rating' }
  ];

  const features = [
    {
      icon: Shield,
      title: '10 Year Structural Warranty',
      description: 'Every shutter we install comes with our market-leading 10 year structural warranty. We stand behind our work because we treat your home like our own.'
    },
    {
      icon: Heart,
      title: 'Family Owned & Operated',
      description: 'We are a family business - not a franchise. From survey to installation, you deal with the same dedicated expert throughout your journey.'
    },
    {
      icon: Ruler,
      title: 'Made To Measure Shutters',
      description: 'We supply & install a beautiful range of shutters in a range of materials and styles to suit any room in your home.'
    }
  ];

  const steps = [
    {
      number: '1',
      title: 'Use Our Online Estimator',
      description: 'Get an instant price estimate using our simple online calculator. Enter your window measurements and preferences.'
    },
    {
      number: '2',
      title: 'Book Your Free Survey',
      description: 'We will visit your home, take professional measurements, and provide a no-obligation quote tailored to your needs.'
    },
    {
      number: '3',
      title: 'Fast, Professional Installation',
      description: 'When you\'re happy with your design, we\'ll book a date with your original surveyor to personally complete the installation.'
    }
  ];

  const faqs = [
    {
      question: 'What Is The Lead Time For Shutters?',
      answer: 'Using our vast supply chain experience and having excellent supplier relationships has allowed us to offer what we believe to be the best pricing for hardwood shutters anywhere in the UK. For hardwood shutters, our current estimated lead time is around 10-12 weeks from order to installation.'
    },
    {
      question: "I've Had Another Quote For Shutters And Yours Is Much Lower, Why Is This?",
      answer: "We like to be upfront with our customers and give them a true indication of what they are paying for. We operate within one of the most exclusive shutter buying groups in the UK which allows us access to factory direct pricing as well as faster lead times. We break down the cost of our shutters and the cost of installation on all of our quotations, meaning you are well aware what you are paying for."
    },
    {
      question: 'How Much Will Our Shutters Cost?',
      answer: "The cost of your shutters will be determined by the size of your windows or doors. We are very transparent about our pricing and believe our pricing is the best the industry has to offer for a fully surveyed and installed project."
    },
    {
      question: 'What Are Your Payment Terms?',
      answer: "Our payment terms are split between the 'Product Cost' (payable at time of order) and an 'Installation Cost' (payable on completed installation). We would never request any full balance upfront and instead prefer you to be satisfied with the installation after demonstrating your new shutters to you."
    },
    {
      question: 'Who Will Install Our Shutters?',
      answer: "We are a family owned, husband and wife ran business that is named after our children. We handle everything from initial survey to installation ourselves - we do not use any subcontractors."
    },
    {
      question: 'Do Shutters Prevent Heat Loss?',
      answer: "You will notice a vast difference once shutters are installed to the heat retention in your home. Shutters offer a great level of insulation when preventing heat loss and also in the warmer months, your home will feel a lot cooler. Shutters also offer great sound insulation qualities."
    },
    {
      question: 'Do Shutters Block Out Light?',
      answer: "Shutters do a good job of blocking out light and many customers are very happy with their level of light control. We do not offer them as a blackout solution. If this is something you require, then you should consider our room darkening shutter solution with integrated blinds."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Top Contact Bar */}
      <div className="bg-white border-b border-gray-100 py-3">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_window-quotes-1/artifacts/kzwoymfj_Aston%20Lily%20Shutters%20Web%20Banner%20.png" 
              alt="Aston Lily Shutters" 
              className="h-16 md:h-20"
            />
          </div>
          <div className="hidden md:flex items-center gap-6">
            <a href="tel:01onal314990" className="flex items-center gap-2 text-gray-700 hover:text-gray-900">
              <Phone className="h-4 w-4" style={{color: '#8CB89F'}} />
              <span className="font-semibold">01275 314990</span>
            </a>
            <a href="mailto:orders@astonlily.co.uk" className="flex items-center gap-2 text-gray-700 hover:text-gray-900">
              <Mail className="h-4 w-4" style={{color: '#8CB89F'}} />
              <span>orders@astonlily.co.uk</span>
            </a>
          </div>
        </div>
      </div>

      {/* Trust Badge Bar */}
      <div className="py-3" style={{backgroundColor: '#8CB89F'}}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center md:justify-between items-center gap-4 text-white">
            {trustBadges.map((badge, index) => (
              <div key={index} className="flex items-center gap-2">
                <badge.icon className="h-5 w-5" />
                <span className="font-medium text-sm md:text-base">{badge.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/79f2e9a3e46d63c12d78f97eafcab5112c7aa1e3bfaeb76a3fc34cf30ef86b44.png)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/80 to-white/40"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            <p className="text-gray-600 text-lg mb-4">Homeowners in South West England and Wales</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Transform Your Home with Beautiful, Made to Measure Shutters
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Enhance your space with expertly crafted, custom shutters
            </p>
            
            {/* Google Rating */}
            <div className="flex items-center gap-3 mb-8">
              <div className="flex items-center gap-1">
                <span className="text-2xl font-bold text-blue-500">G</span>
                <span className="text-2xl font-bold text-red-500">o</span>
                <span className="text-2xl font-bold text-yellow-500">o</span>
                <span className="text-2xl font-bold text-blue-500">g</span>
                <span className="text-2xl font-bold text-green-500">l</span>
                <span className="text-2xl font-bold text-red-500">e</span>
              </div>
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map((star) => (
                  <Star key={star} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
            </div>
            <p className="text-gray-700 font-semibold">
              <span className="font-bold">Rated Excellent</span> on Google
            </p>
          </div>

          {/* Right CTA Card */}
          <div className="lg:justify-self-end w-full max-w-md" ref={quoteFormRef}>
            <Card className="shadow-2xl border-0 overflow-hidden">
              <div className="p-1" style={{backgroundColor: '#8CB89F'}}>
                <p className="text-white text-center text-sm font-medium py-2">
                  👇 Get your Free Quote in under 60 seconds
                </p>
              </div>
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                  How many windows need quoting?
                </h3>
                
                <div className="space-y-3 mb-6">
                  {['1-2 Windows', '3-4 Windows', '5-6 Windows', 'Front of my house', 'My whole house'].map((option, index) => (
                    <button 
                      key={index} 
                      onClick={() => handleWindowSelect(option)}
                      className="w-full text-left"
                    >
                      <div className="border-2 border-gray-200 rounded-lg p-4 hover:border-[#8CB89F] hover:bg-[#8CB89F]/5 transition-all cursor-pointer flex items-center gap-3">
                        <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0"></div>
                        <span className="text-gray-700 font-medium">{option}</span>
                      </div>
                    </button>
                  ))}
                </div>

                <Button 
                  size="lg" 
                  className="w-full text-white text-lg py-6"
                  style={{backgroundColor: '#8CB89F'}}
                  onClick={() => handleWindowSelect('Not specified')}
                >
                  <Calculator className="mr-2 h-5 w-5" />
                  Get Your Free Quote
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                
                <p className="text-center text-sm text-gray-500 mt-4">
                  + Free Shutters Guide
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Choice Modal */}
      <Dialog open={showChoiceModal} onOpenChange={setShowChoiceModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl text-center">How would you like to proceed?</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {selectedWindowCount && selectedWindowCount !== 'Not specified' && (
              <p className="text-center text-gray-600 mb-6">
                You selected: <span className="font-semibold">{selectedWindowCount}</span>
              </p>
            )}
            
            <div className="space-y-4">
              {/* Option 1: Get Estimate */}
              <button 
                onClick={handleGetEstimate}
                className="w-full text-left"
              >
                <Card className="border-2 border-gray-200 hover:border-[#8CB89F] hover:shadow-lg transition-all cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-full" style={{backgroundColor: 'rgba(140, 184, 159, 0.15)'}}>
                        <Calculator className="h-6 w-6" style={{color: '#8CB89F'}} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          Get an Upfront Estimate
                        </h3>
                        <p className="text-gray-600 text-sm">
                          Provide your window sizes and get an instant price estimate online. Quick and easy!
                        </p>
                        <div className="flex items-center gap-2 mt-3 text-sm" style={{color: '#8CB89F'}}>
                          <Clock className="h-4 w-4" />
                          <span>Takes about 2 minutes</span>
                        </div>
                      </div>
                      <ArrowRight className="h-5 w-5 text-gray-400 mt-1" />
                    </div>
                  </CardContent>
                </Card>
              </button>

              {/* Option 2: Book Home Visit */}
              <button 
                onClick={handleBookVisit}
                className="w-full text-left"
              >
                <Card className="border-2 border-gray-200 hover:border-[#8CB89F] hover:shadow-lg transition-all cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-full" style={{backgroundColor: 'rgba(140, 184, 159, 0.15)'}}>
                        <Calendar className="h-6 w-6" style={{color: '#8CB89F'}} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          Book a Free Home Visit
                        </h3>
                        <p className="text-gray-600 text-sm">
                          Skip the measuring - we'll send an expert to your home to take professional measurements and provide a quote.
                        </p>
                        <div className="flex items-center gap-2 mt-3 text-sm" style={{color: '#8CB89F'}}>
                          <Users className="h-4 w-4" />
                          <span>No obligation survey</span>
                        </div>
                      </div>
                      <ArrowRight className="h-5 w-5 text-gray-400 mt-1" />
                    </div>
                  </CardContent>
                </Card>
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg text-center">
                <CardContent className="p-8">
                  <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{backgroundColor: 'rgba(140, 184, 159, 0.15)'}}>
                    <feature.icon className="h-8 w-8" style={{color: '#8CB89F'}} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-block relative">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
                Why Choose Aston Lily Shutters?
              </h2>
              <div className="absolute -bottom-2 left-0 right-0 h-3" style={{backgroundColor: 'rgba(140, 184, 159, 0.3)', transform: 'skewX(-12deg)'}}></div>
            </div>
          </div>
          
          <div className="prose prose-lg max-w-none text-gray-600 text-center">
            <p className="mb-6">
              <strong>Family owned and operated since 2018</strong>, we are specialist suppliers and installers of 
              <strong> made-to-measure hardwood shutters</strong>, offering a truly tailored service from start to finish. 
              Every shutter is crafted using traditional <strong>mortise and tenon joinery</strong>—no screwed frames—ensuring 
              exceptional durability and a premium finish.
            </p>
            <p className="mb-6">
              We back every installation with our <strong>market-leading 10 year structural warranty</strong>. From the 
              moment of survey to the final installation, you'll deal with <strong>one dedicated expert</strong>, 
              not a revolving door of contractors. This personal approach guarantees clear communication, expert knowledge, 
              and <strong>a flawless fit</strong>, every time.
            </p>
            <p className="mb-6">
              Choose <strong>any style, louvre size, control mechanism or hinge colour</strong>—we'll customise your shutters 
              to suit your exact tastes and needs. With an estimated <strong>10-12 week lead time</strong>, 
              you won't have to wait long to transform your space.
            </p>
            <p>
              We handle everything—<strong>no subcontractors, no mess, and no hassle</strong>. All waste is removed from site 
              and your shutters are professionally installed by the same person who surveyed your project.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Making Shutters as easy as
            </h2>
            <p className="text-5xl font-bold" style={{color: '#8CB89F'}}>1, 2, 3...</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div 
                  className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center text-white text-3xl font-bold"
                  style={{backgroundColor: '#8CB89F'}}
                >
                  {step.number}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              size="lg" 
              className="text-white text-lg px-12 py-6"
              style={{backgroundColor: '#8CB89F'}}
              onClick={scrollToQuoteForm}
            >
              <Calculator className="mr-2 h-5 w-5" />
              Get Your Free Quote
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <p className="text-gray-500 mt-3">+ Free Shutters Guide</p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Got a Question?</h2>
            <p className="text-xl text-gray-600">
              FAQs about us and our shutters. Contact us with any other questions.
            </p>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card key={index} className="border shadow-sm overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                  {openFaq === index ? (
                    <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 text-white" style={{backgroundColor: '#8CB89F'}}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready To Update Your Windows?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Get started below with your free, no-obligation quote
          </p>
          <Button 
            size="lg" 
            className="bg-white hover:bg-gray-100 text-lg px-12 py-6"
            style={{color: '#8CB89F'}}
            onClick={scrollToQuoteForm}
          >
            <Calculator className="mr-2 h-5 w-5" />
            Get Your Free Quote
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <p className="mt-4 opacity-80">+ Free Shutters Guide</p>
        </div>
      </section>

      {/* Footer Contact */}
      <section className="py-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-8">
            <a href="tel:01275314990" className="flex items-center gap-2 hover:opacity-80">
              <Phone className="h-5 w-5" />
              <span className="font-semibold">01275 314990</span>
            </a>
            <a href="mailto:orders@astonlily.co.uk" className="flex items-center gap-2 hover:opacity-80">
              <Mail className="h-5 w-5" />
              <span>orders@astonlily.co.uk</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
